import { hotjarInit } from './lib/utils/hotjar';
import { handleErrorWithSentry } from '@sentry/sveltekit';
import * as Sentry from '@sentry/sveltekit';
import { getEnvironment } from '$lib/utils/environment';
import { getSentryConfig } from '$lib/services/sentry/config';
import { getDeviceId, initializeDeviceId, saveDeviceId } from '$lib/utils/device-id.js';
import { app, setGlobalStore } from '$lib/stores/app/store.js';
import { COOKIES, QUERY_PARAM_KEYS } from '$lib/utils/constants.js';
import { setMeta } from '$lib/stores/app/actions.js';
import Cookies from 'js-cookie';
import { AnalyticsService } from '$lib/services/analytics/index.js';
import { qualifyExperiments } from '$lib/services/experiments/index.js';
import { experimentService } from '$lib/services/experiments/client/index.js';
import { ProductExperienceService } from '$lib/services/product-experience/index.js';
import { get } from 'svelte/store';

Sentry.init({
	...getSentryConfig({
		environment: getEnvironment(),
		// @ts-ignore
		release: __RELEASE_NAME__ || 'local',
		tracesSampleRate: 0.25
	}),
	integrations: [
		Sentry.thirdPartyErrorFilterIntegration({
			filterKeys: ['testing-key'],
			behaviour: 'apply-tag-if-contains-third-party-frames'
		})
	],
	allowUrls: [/https?:\/\/(?:www\.|web\.dev\.)?abcmouse\.com/]
});

export const handleError = handleErrorWithSentry();

hotjarInit();

/**
 * Hook for client init
 */
export async function init() {
	await onAppInit();
}

/**
 * Initializes app
 */
async function onAppInit() {
	const params = new URLSearchParams(window.location.search);
	const linkFromAppPlatform = params.get('linkFromAppPlatform');

	const originalDeviceIdCookie = Cookies.get(COOKIES.DEVICE_ID);

	const isFirstTimeVisit = !getDeviceId();
	initializeDeviceId();
	const deviceId = getDeviceId();

	// Initialize meta values (including inApp detection) BEFORE route conditions run
	// Route conditions depend on config.meta.inApp, so this must happen early
	const isLinkFromEmail = params.get(QUERY_PARAM_KEYS.EMAIL_CLICK) ? 'true' : 'false';
	const metaDeviceId = get(app)?.meta?.deviceId;

	try {
		let processedDeviceIdStore = get(app)?.meta?.deviceIdStore || '';
		if (metaDeviceId !== originalDeviceIdCookie) {
			processedDeviceIdStore += ':UPDATE:' + (deviceId ?? 'empty_device_id');
			if (typeof processedDeviceIdStore === 'string' && processedDeviceIdStore.length > 44 * 68) {
				processedDeviceIdStore = processedDeviceIdStore.substring(44);
			}
		}
		await setMeta({
			...Cookies.get(),
			acquisitionSource: AnalyticsService.getSourceTag() ?? '',
			deviceId,
			deviceIdStore: processedDeviceIdStore,
			isLinkFromEmail: isLinkFromEmail
		});
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'onAppMount setMeta error'
			}
		});
	}

	await setGlobalStore({
		...(linkFromAppPlatform && { linkFromAppPlatform }),
		isFirstTimeVisit,
		environment: getEnvironment()
	});
	await qualifyExperiments();
	await experimentService.initializeExperiments();

	// Ensure product experience is set from URL before conditions resolve
	await ProductExperienceService.setProductExperienceFromQuery();
}